import { useEffect } from 'react';
import { constants } from 'lib_ui-services';
const { sensorTypes } = constants;

/**
 * While continuous scan owns the RFID trigger, reader reads must not reach a setup screen's grid.
 * Shared by both setup screens that hand off to continuous scan: Mass Update
 * (MultiPickerTransactionGrid) and Physical Inventory Count (MarkRecord).
 *
 * The trigger drives two independent consumers: ContinuousScanModule (via
 * ZebraRfid.isInContinuousScanSession) AND, unconditionally, the legacy `RfidScansReceived`
 * JS path (RfidReaderModule.receiveScans emits both - the continuous-scan forward is in
 * addition to the legacy emit, not instead of it). Refusing the hand-off therefore stops the
 * NAVIGATION but not the SCAN: pull the trigger with nothing staged and the reads still land on
 * the setup screen as ordinary sensor reads. On Mass Update that was invisible while the grid was
 * hidden behind `!isContinuousScanEnabled`; now that it always renders (so restored
 * manual/barcode entries survive the round-trip to Screen B), those stray reads show up as rows
 * and the screen reverts to looking like the pre-continuous-scan Mass Update (#1808). The Count
 * screen's SensorReadList has always rendered, so it has the same leak (#1817).
 *
 * Removed rather than filtered at render: rows come from useMergedReads().subscribeToChange,
 * which has no sensorType gate, and the Count reads the same entries - so dropping them at the
 * source is the only place that fixes both.
 *
 * Deferred to a microtask rather than removed inline. The remove has to land AFTER ReadProvider
 * has taken the read, and subscription order does not guarantee that: React runs a child's
 * effects before its ancestors', so the consuming component subscribes to `sensor:read` BEFORE
 * the ReadProvider above it does. Removing synchronously would run first and no-op against a read
 * that has not been added yet. A microtask runs after every synchronous subscriber for this
 * publish, whatever order they registered in, and is still two orders of magnitude inside
 * MergedReadProvider's 200 ms view throttle - so the row never renders.
 *
 * Read identity is taken after ReadProvider has normalised it: addReads mutates the read in
 * place (`read.tagId = rfidStrategy.normalizeTagIdHex(...)`) and stores that same object, and
 * removeRead matches on `tagId`. Reading it in the microtask therefore sees the normalised
 * value that is actually in the store, where reading it inline would see the raw EPC.
 *
 * MANUAL and BARCODE are deliberate user input and are left alone - they are what the
 * always-rendered grid is for.
 *
 * @param {boolean} continuousScanEnabled whether continuous scan owns the trigger on this screen.
 * Mass Update passes `isContinuousScanEnabled`; Count passes `continuousScanAvailable`, which
 * additionally requires a selected room - below that, continuous scan cannot take over and the
 * legacy read path is the real one, so nothing is blocked.
 * @param {Function} subscribe from useEventSink
 * @param {Function} publish from useEventSink
 */
export default function useBlockReaderReadsOnSetupScreen(continuousScanEnabled, subscribe, publish) {
    useEffect(() => {
        if (!continuousScanEnabled) return undefined;
        let cancelled = false;
        const unsubscribe = subscribe({ verb: 'change', namespace: 'sensor', relation: 'read' }, reads => {
            const readerReads = (Array.isArray(reads) ? reads : [reads]).filter(read =>
                [sensorTypes.RFID, sensorTypes.BLE].includes(read?.sensorType)
            );
            if (!readerReads.length) return;
            Promise.resolve().then(() => {
                if (cancelled) return;
                readerReads.forEach(read => {
                    publish({ tagId: read.tagId }, { verb: 'remove', namespace: 'sensor', relation: 'read' });
                });
            });
        });
        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, [continuousScanEnabled, subscribe, publish]);
}
